import { Injectable } from '@angular/core';
import { Job } from '../types/job.types';

interface CachedPageData {
  jobs: Job[];
  totalCount: number;
  totalPages: number;
  timestamp: number;
  filters: PageFilters;
}

interface PageFilters {
  category?: string | null;
  searchQuery?: string;
  locationQuery?: string;
  showRemoteOnly?: boolean;
  sortBy?: string;
}

interface PreloadRequest {
  page: number;
  filters: PageFilters;
  loadFunction: (page: number) => Promise<{ jobs: Job[]; totalCount: number; totalPages: number }>;
}

@Injectable({
  providedIn: 'root'
})
export class PaginationPreloaderService {
  private cache = new Map<string, CachedPageData>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 20; // Maximum number of cached pages
  private preloadQueue: PreloadRequest[] = [];
  private isPreloading = false;

  constructor() {}

  /**
   * Generate a cache key based on page number and filters
   */
  private generateCacheKey(page: number, filters: PageFilters): string {
    return `page_${page}_${JSON.stringify(filters)}`;
  }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(cachedData: CachedPageData): boolean {
    const now = Date.now();
    return (now - cachedData.timestamp) < this.CACHE_DURATION;
  }

  /**
   * Clean up expired cache entries and manage cache size
   */
  private cleanupCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    // Remove expired entries
    for (const [key, data] of this.cache.entries()) {
      if (!this.isCacheValid(data)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));

    // If cache is still too large, remove oldest entries
    if (this.cache.size > this.MAX_CACHE_SIZE) {
      const sortedEntries = Array.from(this.cache.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp);
      
      const entriesToRemove = sortedEntries.slice(0, this.cache.size - this.MAX_CACHE_SIZE);
      entriesToRemove.forEach(([key]) => this.cache.delete(key));
    }
  }

  /**
   * Get cached data for a specific page and filters
   */
  getCachedData(page: number, filters: PageFilters): CachedPageData | null {
    this.cleanupCache();
    const cacheKey = this.generateCacheKey(page, filters);
    const cachedData = this.cache.get(cacheKey);
    
    if (!cachedData) {
      return null;
    }

    // Check if cache is still valid
    if (Date.now() - cachedData.timestamp > this.CACHE_DURATION) {
      this.cache.delete(cacheKey);
      return null;
    }

    return cachedData;
  }

  /**
   * Cache data for a specific page and filters
   */
  cacheData(page: number, filters: PageFilters, jobs: Job[], totalCount: number, totalPages: number): void {
    const cacheKey = this.generateCacheKey(page, filters);
    const cachedData: CachedPageData = {
      jobs,
      totalCount,
      totalPages,
      timestamp: Date.now(),
      filters: { ...filters }
    };
    
    this.cache.set(cacheKey, cachedData);
    this.cleanupCache();
  }

  /**
   * Check if specific data is cached
   */
  isCached(page: number, filters: PageFilters): boolean {
    return this.getCachedData(page, filters) !== null;
  }

  /**
   * Preload adjacent pages in the background
   */
  preloadAdjacentPages(
    currentPage: number, 
    totalPages: number, 
    filters: PageFilters,
    loadFunction: (page: number) => Promise<{ jobs: Job[]; totalCount: number; totalPages: number }>
  ): void {
    const pagesToPreload: number[] = [];

    // Determine which pages to preload based on current page
    if (currentPage === 1 && totalPages > 1) {
      // On page 1, preload page 2
      pagesToPreload.push(2);
    } else if (currentPage === 2 && totalPages > 2) {
      // On page 2, preload pages 3 and 4
      if (totalPages >= 3) pagesToPreload.push(3);
      if (totalPages >= 4) pagesToPreload.push(4);
    } else if (currentPage > 2) {
      // On other pages, preload next 2 pages
      if (currentPage + 1 <= totalPages) pagesToPreload.push(currentPage + 1);
      if (currentPage + 2 <= totalPages) pagesToPreload.push(currentPage + 2);
    }

    // Add pages to preload queue if not already cached
    for (const page of pagesToPreload) {
      if (!this.isCached(page, filters)) {
        const existingIndex = this.preloadQueue.findIndex(
          req => req.page === page && JSON.stringify(req.filters) === JSON.stringify(filters)
        );
        
        if (existingIndex === -1) {
          this.preloadQueue.push({ page, filters: { ...filters }, loadFunction });
        }
      }
    }

    // Start preloading if not already in progress
    this.processPreloadQueue();
  }

  /**
   * Process the preload queue
   */
  private async processPreloadQueue(): Promise<void> {
    if (this.isPreloading || this.preloadQueue.length === 0) {
      return;
    }

    this.isPreloading = true;

    while (this.preloadQueue.length > 0) {
      const request = this.preloadQueue.shift();
      if (!request) break;

      const cacheKey = this.generateCacheKey(request.page, request.filters);
      if (this.cache.has(cacheKey)) {
        continue;
      }

      try {
        const startTime = Date.now();
        const result = await request.loadFunction(request.page);
        const endTime = Date.now();
        this.cacheData(request.page, request.filters, result.jobs, result.totalCount, result.totalPages);
      } catch (error) {
        // Error handling is silent in background preloading
      }

      // Add small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    this.isPreloading = false;
  }

  /**
   * Clear all cached data (useful when major filters change)
   */
  clearCache(): void {
    this.cache.clear();
    this.preloadQueue = [];
  }

  /**
   * Clear cache for specific filters (useful when minor filters change)
   */
  clearCacheForFilters(filters: PageFilters): void {
    const keysToDelete: string[] = [];
    
    for (const [key, data] of this.cache.entries()) {
      if (JSON.stringify(data.filters) === JSON.stringify(filters)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
    
    // Remove matching items from preload queue
    this.preloadQueue = this.preloadQueue.filter(
      req => JSON.stringify(req.filters) !== JSON.stringify(filters)
    );
  }
} 